import React, { ReactElement, useState } from "react";
import {
    HsvColor,
    hexToHsv,
    hsvToHex,
    isValidHexColor,
    normalizeHexColor,
} from "@common/helpers/color";
import { PipetteIcon } from "@icons";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { ColorSaturationArea } from "./ColorSaturationArea";
import styles from "./BackgroundColorPicker.module.scss";

interface Props {
    value: string;
    onChange: (hex: string) => void;
    children: ReactElement;
    // Reused for both the Background and Font color custom swatches (same
    // HSV wheel + hex field, different accessible name for the popover).
    ariaLabel?: string;
}

type Draft =
    | { type: "hsv"; hsv: HsvColor }
    | { type: "hex"; text: string; startedFrom: string };

const canUseEyeDropper =
    typeof window !== "undefined" && window.EyeDropper !== undefined;

function hsvForValue(value: string, draft: Draft | null): HsvColor {
    return draft?.type === "hsv" && hsvToHex(draft.hsv) === value
        ? draft.hsv
        : hexToHsv(value);
}

function hexTextForValue(value: string, draft: Draft | null): string {
    if (draft?.type !== "hex") {
        return value;
    }
    if (
        isValidHexColor(draft.text) &&
        normalizeHexColor(draft.text) === value
    ) {
        return draft.text;
    }
    return draft.startedFrom === value ? draft.text : value;
}

async function pickHexFromScreen(): Promise<string | null> {
    if (window.EyeDropper === undefined) {
        return null;
    }
    try {
        const { sRGBHex } = await new window.EyeDropper().open();
        return sRGBHex;
    } catch {
        return null;
    }
}

export const BackgroundColorPicker: React.FC<Props> = ({
    value,
    onChange,
    children,
    ariaLabel = "Custom background color",
}) => {
    const [draft, setDraft] = useState<Draft | null>(null);
    const hsv = hsvForValue(value, draft);
    const hexText = hexTextForValue(value, draft);

    const commitHsv = (next: HsvColor) => {
        setDraft({ type: "hsv", hsv: next });
        onChange(hsvToHex(next));
    };

    const commitHex = (next: string) => {
        setDraft({ type: "hex", text: next, startedFrom: value });
        if (isValidHexColor(next)) {
            onChange(normalizeHexColor(next));
        }
    };

    return (
        <Popover>
            <PopoverTrigger>{children}</PopoverTrigger>
            <PopoverContent
                align="start"
                className={styles.pickerPanel}
                aria-label={ariaLabel}
            >
                <ColorSaturationArea hsv={hsv} onChange={commitHsv} />
                <div className={styles.pickerControls}>
                    {canUseEyeDropper && (
                        <button
                            type="button"
                            className={styles.eyedropperButton}
                            aria-label="Pick a color from the screen"
                            title="Pick a color from the screen"
                            onClick={() => {
                                void pickHexFromScreen().then((hex) => {
                                    if (hex !== null) {
                                        commitHex(hex);
                                    }
                                });
                            }}
                        >
                            <PipetteIcon />
                        </button>
                    )}
                    <span
                        className={styles.preview}
                        style={{ background: hsvToHex(hsv) }}
                    />
                    <input
                        type="range"
                        min={0}
                        max={360}
                        step={1}
                        className={styles.hueSlider}
                        aria-label="Hue"
                        value={Math.round(hsv.hue)}
                        onChange={(event) =>
                            commitHsv({
                                ...hsv,
                                hue: Number(event.target.value),
                            })
                        }
                    />
                </div>
                <label className={styles.hexField}>
                    <input
                        type="text"
                        className={styles.hexInput}
                        value={hexText}
                        spellCheck={false}
                        maxLength={7}
                        placeholder="#000000"
                        onChange={(event) => commitHex(event.target.value)}
                        onBlur={() => setDraft(null)}
                    />
                    <span className={styles.hexLabel}>HEX</span>
                </label>
            </PopoverContent>
        </Popover>
    );
};
